import { FC, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Switch } from '@headlessui/react';

import Drawer from 'components/Drawer/Drawer';
import CommonButton from 'components/CommonButton/CommonButton';
import { renderToast } from 'components/Toast/Toast';
import useClinic from 'hooks/useClinic';
import axiosInstance from 'apis/axiosInstance';
import { PaymentHoldApplyTo } from 'interfaces/stripe';
import PaymentSettingsForm, {
  PaymentSettingsFormValues,
} from './PaymentSettingsForm';

interface PaymentSettingsDrawerProps {
  isShowing: boolean;
  setIsShowing: React.Dispatch<React.SetStateAction<boolean>>;
}

const DEFAULT_BOOKING_DEPOSIT_AMOUNT = 50;

const PaymentSettingsDrawer: FC<PaymentSettingsDrawerProps> = ({
  isShowing,
  setIsShowing,
}) => {
  const { data: clinic, mutate } = useClinic();

  const { control, watch, handleSubmit, reset, formState } =
    useForm<PaymentSettingsFormValues>({
      defaultValues: {
        isStripeCheckoutEnabled: false,
        paymentHoldApplyTo: PaymentHoldApplyTo.ALL_PATIENTS,
        bookingDepositAmount: DEFAULT_BOOKING_DEPOSIT_AMOUNT,
        paymentHoldWithinDays: null,
      },
    });

  const isEnabled = watch('isStripeCheckoutEnabled');

  const resetToClinic = useCallback(() => {
    reset({
      isStripeCheckoutEnabled: !!clinic?.isStripeCheckoutEnabled,
      paymentHoldApplyTo:
        clinic?.paymentHoldApplyTo ?? PaymentHoldApplyTo.ALL_PATIENTS,
      bookingDepositAmount:
        Number(clinic?.bookingDepositAmount) || DEFAULT_BOOKING_DEPOSIT_AMOUNT,
      paymentHoldWithinDays: clinic?.paymentHoldWithinDays ?? null,
    });
  }, [clinic, reset]);

  useEffect(() => {
    resetToClinic();
  }, [resetToClinic]);

  const onSave = async (values: PaymentSettingsFormValues) => {
    try {
      await axiosInstance.patch('/clinics/stripe-checkout-setting', {
        isStripeCheckoutEnabled: values.isStripeCheckoutEnabled,
        ...(values.isStripeCheckoutEnabled && {
          bookingDepositAmount: values.bookingDepositAmount,
          paymentHoldApplyTo: values.paymentHoldApplyTo,
          paymentHoldWithinDays: values.paymentHoldWithinDays,
        }),
      });
      await mutate();
      reset(values);
      renderToast({
        type: 'success',
        message: 'Payment settings updated successfully',
      });
      setIsShowing(false);
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  const onCancel = () => {
    resetToClinic();
    setIsShowing(false);
  };

  return (
    <Drawer
      title="Payment Settings"
      isShowing={isShowing}
      setIsShowing={setIsShowing}
      onReset={resetToClinic}
      footer={
        <div className="w-full h-6 flex px-2.4 justify-end items-center shadow-primary sticky bottom-0 bg-white gap-x-1.6">
          <CommonButton
            variant="secondary"
            onClick={onCancel}
            disabled={formState.isSubmitting}
          >
            Cancel
          </CommonButton>
          <CommonButton
            onClick={handleSubmit(onSave)}
            disabled={!formState.isDirty}
            isLoading={formState.isSubmitting}
          >
            Save
          </CommonButton>
        </div>
      }
    >
      <div className="flex flex-col gap-y-3.2">
        <section className="flex flex-col gap-y-1.6">
          <p className="font-bold text-16 leading-[2.4rem] text-darkest-grey">
            Payment Authorization Feature
          </p>
          <Controller
            name="isStripeCheckoutEnabled"
            control={control}
            render={({ field: { value, onChange } }) => (
              <div className="flex items-center gap-x-1.2">
                <Switch
                  checked={value}
                  onChange={onChange}
                  className={`${
                    value ? 'bg-magenta' : 'bg-[#78788029]'
                  } relative inline-flex h-3.1 w-5.1 shrink-0 items-center rounded-full`}
                >
                  <span
                    className={`${
                      value ? 'translate-x-[2.3rem]' : 'translate-x-[2px]'
                    } inline-block h-2.5 w-2.5 transform rounded-full bg-white transition`}
                  />
                </Switch>
                <span className="text-16 leading-[2.4rem] text-mid-grey">
                  {value ? 'ON' : 'OFF'}
                </span>
              </div>
            )}
          />
        </section>

        {isEnabled && <PaymentSettingsForm control={control} />}
      </div>
    </Drawer>
  );
};

export default PaymentSettingsDrawer;
